import React, { useState } from 'react';
import {
  Box, Button, Typography, CircularProgress, TextField,
  InputAdornment, IconButton, Divider,
} from '@mui/material';
import LocalMoviesIcon from '@mui/icons-material/LocalMovies';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useAuth } from '../AuthContext';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: 'rgba(255,255,255,0.04)',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
    '&:hover fieldset': { borderColor: 'rgba(232,184,109,0.4)' },
    '&.Mui-focused fieldset': { borderColor: '#e8b86d' },
  },
  '& .MuiInputLabel-root': { color: '#7a6a4a' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#e8b86d' },
  '& .MuiInputBase-input': { color: '#f5e6c8' },
};

export default function SignInScreen() {
  const { signIn, signInEmail } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSignIn = async () => {
    setLoading(true);
    setError('');
    try {
      await signIn();
    } catch (e) {
      if (e.code !== 'auth/popup-closed-by-user') {
        setError('Sign-in failed. Make sure you are using an authorised account.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await signInEmail(email, password);
    } catch {
      setError('Sign-in failed. Please check your email and password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#111008',
        px: 3,
      }}
    >
      {/* Logo */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <LocalMoviesIcon sx={{ fontSize: 28, color: '#e8b86d' }} />
        <Box>
          <Typography sx={{
            fontSize: '1.1rem', fontWeight: 700, color: '#f5e6c8', lineHeight: 1.15,
            fontFamily: '"Playfair Display", serif',
          }}>
            British Show Butler
          </Typography>
          <Typography sx={{
            fontSize: '0.55rem', letterSpacing: 2.5, textTransform: 'uppercase',
            color: '#7a6a4a', fontFamily: '"Inter", sans-serif',
          }}>
            Your Personal Detective Concierge
          </Typography>
        </Box>
      </Box>

      <Typography sx={{
        fontSize: '0.82rem', color: '#7a6a4a', mb: 4.5,
        fontStyle: 'italic', fontFamily: '"Playfair Display", serif', textAlign: 'center',
      }}>
        "Every great detective needs the right case files."
      </Typography>

      <Box sx={{ width: '100%', maxWidth: 320, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* Primary: Google */}
        <Button
          variant="contained"
          onClick={handleSignIn}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ py: 1.25, textTransform: 'none', fontSize: '0.95rem', borderRadius: 1.5, fontWeight: 600 }}
        >
          {loading ? 'Signing in…' : 'Continue with Google'}
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 0.5 }}>
          <Divider sx={{ flex: 1, borderColor: 'rgba(255,255,255,0.08)' }} />
          <Typography sx={{ fontSize: '0.65rem', color: '#7a6a4a', textTransform: 'uppercase', letterSpacing: 1 }}>
            or
          </Typography>
          <Divider sx={{ flex: 1, borderColor: 'rgba(255,255,255,0.08)' }} />
        </Box>

        {/* Email / password — always visible */}
        <Box component="form" onSubmit={handleEmailSignIn} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <TextField
            label="Email"
            type="email"
            variant="outlined"
            fullWidth
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            size="small"
            sx={fieldSx}
          />
          <TextField
            label="Password"
            type={showPassword ? 'text' : 'password'}
            variant="outlined"
            fullWidth
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            size="small"
            sx={fieldSx}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(v => !v)}
                    edge="end"
                    size="small"
                    tabIndex={-1}
                    sx={{ color: '#7a6a4a', mr: -0.5 }}
                  >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            variant="outlined"
            disabled={loading || !email || !password}
            sx={{
              py: 1.1, textTransform: 'none', borderRadius: 1.5,
              borderColor: 'rgba(232,184,109,0.35)', color: '#e8b86d',
              '&:hover': { borderColor: '#e8b86d', bgcolor: 'rgba(232,184,109,0.06)' },
              '&.Mui-disabled': { borderColor: 'rgba(255,255,255,0.1)', color: '#7a6a4a' },
            }}
          >
            {loading ? 'Signing in…' : 'Log in with email'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Typography sx={{ color: 'error.main', fontSize: '0.82rem', textAlign: 'center', maxWidth: 320, mt: 2 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}
